import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Flatpickr from "react-flatpickr";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { format, subDays } from "date-fns";
import { FaUserCheck, FaUserTimes, FaUserCircle } from "react-icons/fa";
import { HiOutlineBars3, HiOutlineSquares2X2 } from "react-icons/hi2";
import {
  FaEnvelope,
  FaBuilding,
  FaCity,
  FaUsers,
  FaPhoneAlt,
  FaGlobe,
} from "react-icons/fa";
import qs from "qs";
import "../../styles/TeamsMemberList.css";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import {
  MdOutlineDeviceUnknown,
  MdOutlineNotificationsActive,
} from "react-icons/md";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

interface Reportee {
  user_uid: string;
  user_name: string;
  user_status: string;
  image_uid?: string;
}

const ViewReportees: React.FC = () => {
  useCheckSessionHeartBeat();
  const [reportees, setReportees] = useState<Reportee[]>([]);
  const [visibleCount, setVisibleCount] = useState<number>(20);
  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 6), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [viewMode, setViewMode] = useState<"row" | "card">("card");
  const [selectedUserProfile, setSelectedUserProfile] = useState<any>(null);
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const [userImages, setUserImages] = useState<{ [user_uid: string]: string }>(
    {}
  );
  const fetchedOnce = useRef<Set<string>>(new Set());

  const location = useLocation();
  const targetUserUid = (location.state as any)?.target_user_uid;
  const leaderName = (location.state as any)?.leader_name || "Leader";

  const session_uid = localStorage.getItem("session_uid")!;
  const user_uid = localStorage.getItem("user_uid")!;
  const [visibleUids, setVisibleUids] = useState<Set<string>>(new Set());

  const observer = useRef<IntersectionObserver | null>(null);

  const navigate = useNavigate();

  const fetchReportees = async () => {
    setIsLoading(true);
    try {
      const from = `${startDate}T00:00:00Z`;
      const to = `${endDate}T00:00:00Z`;

      const query = new URLSearchParams({
        from_datetime: from,
        to_datetime: to,
        target_user_uid: targetUserUid!,
      }).toString();

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/get-hierarchy-user-details?${query}`,
        {
          headers: {
            "user-uid": user_uid,
          },credentials: "include",
        }
      );

      const json = await res.json();
      setReportees(json.data || []);
      SessionExtend();
    } catch (err) {
      console.error("Failed to load reportees", err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredReportees = useMemo(() => {
    return reportees
      .sort((a, b) => {
        if (a.user_status === "active" && b.user_status !== "active") return -1;
        if (a.user_status !== "active" && b.user_status === "active") return 1;
        return 0;
      })
      .filter((user) =>
        user.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .filter((user) => {
        if (showActiveOnly) return user.user_status === "active";
        if (showInactiveOnly) return user.user_status !== "active";
        return true;
      });
  }, [reportees, searchQuery, showActiveOnly, showInactiveOnly]);

  useEffect(() => {
    if (targetUserUid) {
      fetchReportees();
    }
  }, [targetUserUid, startDate, endDate]);

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        const newlyVisible = new Set<string>();
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const uid = entry.target.getAttribute("data-uid");
            if (uid) newlyVisible.add(uid);
          }
        });
        if (newlyVisible.size > 0) {
          setVisibleUids((prev) => new Set([...prev, ...newlyVisible]));
        }
      },
      { rootMargin: "100px" }
    );
  }, []);

  useEffect(() => {
    const newlyVisible = Array.from(visibleUids).filter(
      (uid) => !userImages[uid] && !fetchedOnce.current.has(uid)
    );

    if (newlyVisible.length > 0) {
      newlyVisible.forEach((uid) => fetchedOnce.current.add(uid));
      fetchUserImages(newlyVisible);
    }
  }, [visibleUids]);


  const getImageSrc = (image_uid?: string) => {
    if (!image_uid || image_uid === "default_black_image_base64") {
      return "/images/default_user.png";
    }
    return `/api/user-profile-image/${image_uid}`;
  };

  const fetchUserProfile = async (
    targetUserUid: string,
    iconImage?: string
  ) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/public-user-profile`,
        {
          headers: {
            "user-uid": user_uid!,
            "Content-Type": "application/json",
          },
          params: {
            target_user_uid: targetUserUid,
          },withCredentials: true,
        }
      );

      if (
        response.data.status === "Success" &&
        typeof response.data.data === "object"
      ) {
        const userData = response.data.data;

        if (iconImage && iconImage.trim() !== "") {
          if (iconImage.startsWith("data:")) {
            userData.image = iconImage;
          } else {
            let mimeType = "image/png";
            if (iconImage.startsWith("R0lGODlh")) mimeType = "image/gif";
            else if (iconImage.startsWith("/9j/")) mimeType = "image/jpeg";
            else if (iconImage.startsWith("iVBORw0KGgo"))
              mimeType = "image/png";

            userData.image = `data:${mimeType};base64,${iconImage}`;
          }
        }
        setSelectedUserProfile(userData);
        setIsProfilePopupOpen(true);
      } else {
        console.error("Invalid profile response", response.data);
      }
      SessionExtend();
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };
  const fetchUserImages = async (userUids: string[]) => {
    const uidsToFetch = userUids.filter((uid) => !userImages[uid]);
    if (uidsToFetch.length === 0) return;

    try {
      const queryString = qs.stringify(
        {
          icon_img: true,
          target_user_uid_list: uidsToFetch,
        },
        { arrayFormat: "repeat" }
      );

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user-profile-images?${queryString}`,
        {
          headers: {
            "user-uid": localStorage.getItem("user_uid") || "",
          },withCredentials: true,
          responseType: "json",
        }
      );
      const blankGif =
        "R0lGODlhAQABAIABAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAICRAEAOw==";
      const imageMap: Record<string, string> = {};

      response.data?.data?.forEach((entry: any) => {
        const base64 = entry.image;
        if (entry.user_uid && base64 && base64 !== blankGif) {
          let mimeType = "image/png"; // default

          if (base64.startsWith("R0lGODlh")) mimeType = "image/gif";
          else if (base64.startsWith("/9j/")) mimeType = "image/jpeg";
          else if (base64.startsWith("iVBORw0KGgo")) mimeType = "image/png";

          imageMap[entry.user_uid] = `data:${mimeType};base64,${base64}`;
        }
      });

      setUserImages((prev) => ({ ...prev, ...imageMap }));
      SessionExtend();
    } catch (error) {
      console.warn("Failed to fetch user images", error);
    }
  };

  //  Scroll listener to load next 20 when near bottom
  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      setVisibleCount((prev) => Math.min(prev + 20, reportees.length));
    }
  }, [reportees.length]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "active":
        return { color: "#16821aff", label: "Online", iconname: "checkcircle" }; // green 16821aff
      case "away":
        return { color: "#f6c70aff", label: "Away", iconname: "clockcircle" }; // yellow f6c70aff
      case "invited":
        return {
          color: "#000000",
          label: "Inactive",
          iconname: "exclamationcircle",
        }; // black
      default:
        return {
          color: "#9b9292ff",
          label: "Offline",
          iconname: "closecircle",
        }; // black
    }
  };
  return (
    <div className="ManageTeamList-full-container">
      <div className="ManageTeamList-summary-enhanced">
        <div className="ManageTeamList-title">
          <h2>Reportees of {leaderName}</h2>
        </div>

        <div className="ManageTeamList-stats">
          <div className="ManageTeamList-stat-card total">
            <span className="label">Total Members</span>
            <span className="value">{reportees.length}</span>
          </div>
          <div className="ManageTeamList-stat-card active">
            <span className="label">Registered</span>
            <span className="value">
              {reportees.filter((u) => u.user_status === "active").length}
            </span>
          </div>
          <div className="ManageTeamList-stat-card inactive">
            <span className="label">Unregistered</span>
            <span className="value">
              {reportees.filter((u) => u.user_status !== "active").length}
            </span>
          </div>
        </div>
      </div>

      <div className="ManageTeamList-members-header">
        <div className="ManageTeamList-top-controls">
          {/* <div className="ManageTeamList-date-picker">
            <label>From:</label>
            <Flatpickr
              value={startDate}
              options={{ dateFormat: "Y-m-d", maxDate: new Date() }}
              onChange={(dates) => dates[0] && setStartDate(format(dates[0], "yyyy-MM-dd"))}
            />
            <label>To:</label>
            <Flatpickr
              value={endDate}
              options={{ dateFormat: "Y-m-d", maxDate: new Date() }}
              onChange={(dates) => dates[0] && setEndDate(format(dates[0], "yyyy-MM-dd"))}
            />
          </div> */}

          <div className="ManageTeamList-filter-bar">
            <label className="filter-toggle">
              <input
                type="checkbox"
                checked={showActiveOnly}
                onChange={(e) => {
                  setShowActiveOnly(e.target.checked);
                  if (e.target.checked) setShowInactiveOnly(false);
                }}
              />
              Show Registered Only
            </label>
            <label className="filter-toggle">
              <input
                type="checkbox"
                checked={showInactiveOnly}
                onChange={(e) => {
                  setShowInactiveOnly(e.target.checked);
                  if (e.target.checked) setShowActiveOnly(false);
                }}
              />
              Show Unregistered Only
            </label>
          </div>
          <div className="ManageTeamList-view-toggle">
            <button
              className={`ManageTeamList-view-btn ${viewMode === "row" ? "active" : ""
                }`}
              onClick={() => setViewMode("row")}
            >
              <HiOutlineBars3 size={18} /> Row
            </button>
            <button
              className={`ManageTeamList-view-btn ${viewMode === "card" ? "active" : ""
                }`}
              onClick={() => setViewMode("card")}
            >
              <HiOutlineSquares2X2 size={18} /> Card
            </button>
          </div>
          <button
            className="ManageTeamList-view-leaderboard-btn"
            onClick={() =>
              navigate("/reportees-leaderboard", {
                state: {
                  target_user_uid: targetUserUid,
                  group_name: "Reportees",
                  leader_name: reportees[0]?.user_name || "Leader",
                },
              })
            }
          >
            View Team Leaderboard
          </button>

          <div className="ManageTeamList-search-container">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="ManageTeamList-search-input"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="ManageTeamList-loader">Loading reportees...</div>
      ) : filteredReportees.length === 0 ? (
        <p style={{ textAlign: "center", marginTop: "3rem" }}>
          No reportees found.
        </p>
      ) : (
        <div
          className={`ManageTeamList-members-rows ${viewMode === "card" ? "card-view" : "row-view"
            }`}
        >
          {filteredReportees.map((user, index) => (
            <div
              key={index}
              data-uid={user.user_uid}
              ref={(el) => {
                if (el && observer.current) {
                  observer.current.observe(el);
                }
              }}
              className={`ManageTeamList-user-card ${user.user_status !== "active" ? "inactive-user" : ""
                }`}
              title={user.user_status !== "active" ? "Unregistered User" : ""}
              onClick={() =>
                fetchUserProfile(user.user_uid, userImages[user.user_uid])
              }
            >
              {userImages[user.user_uid] ? (
                <img
                  src={userImages[user.user_uid]}
                  alt={user.user_name}
                  className="ManageTeamList-user-icon large"
                />
              ) : (
                <FaUserCircle className="ManageTeamList-fallback-icon large" />
              )}
              <h4>{user.user_name}</h4>
              <p
                className={`status-tag ${user.user_status === "active"
                    ? "MemeberListregistered"
                    : "MemeberListunregistered"
                  }`}
              >
                {user.user_status === "active" ? "Registered" : "Unregistered"}
              </p>
            </div>
          ))}
        </div>
      )}
      {isProfilePopupOpen && selectedUserProfile && (
        <div className="member-profile-popup">
          <div className="member-profile-popup-content">
            <span
              className="member-profile-close"
              onClick={() => setIsProfilePopupOpen(false)}
            >
              &times;
            </span>

            <div className="member-profile-header">
              {selectedUserProfile.image ? (
                <img
                  src={selectedUserProfile.image}
                  alt="Profile"
                  className="member-profile-image"
                  style={{
                    border: `3px solid ${getStatusInfo(selectedUserProfile.user_online_status)
                        .color
                      }`,
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <FaUserCircle
                  className="member-profile-placeholder"
                  style={{
                    color: getStatusInfo(selectedUserProfile.user_online_status)
                      .color,
                    border: `3px solid ${getStatusInfo(selectedUserProfile.user_online_status)
                        .color
                      }`,
                    borderRadius: "50%",
                    padding: "3px",
                  }}
                />
              )}
              <div
                style={{
                  textAlign: "center",
                  marginTop: "2px",
                  fontWeight: "500",
                  color: getStatusInfo(selectedUserProfile.user_online_status)
                    .color,
                }}
              >
                {getStatusInfo(selectedUserProfile.user_online_status).label}
              </div>
              <div className="member-name-job">
                <h2>{`${selectedUserProfile.first_name} ${selectedUserProfile.last_name}`}</h2>
                <p className="member-job-title">
                  {selectedUserProfile.job_title}
                </p>
                <p
                  className={`status-label ${selectedUserProfile.user_status === "active"
                      ? "active"
                      : "inactive"
                    }`}
                >
                  {selectedUserProfile.user_status === "active" ? (
                    <span className="status-tag MemeberListregistered">
                      Registered
                    </span>
                  ) : (
                    <span className="status-tag MemeberListunregistered">
                      Unregistered
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="leaderboard-member-details">
              <div className="leaderboard-popup-row">
                <FaEnvelope className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Email:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.email}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaBuilding className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Department:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.department}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaPhoneAlt className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Mobile:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.mobile_phone || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaGlobe className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Nationality:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.nationality || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaCity className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">City:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.city || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaUsers className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Team:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.team || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <MdOutlineDeviceUnknown className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Device type:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.device_type || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <MdOutlineNotificationsActive className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Last Active:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile?.last_active
                      ? new Date(
                        selectedUserProfile.last_active
                      ).toLocaleString()
                      : "Never logged in"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewReportees;
